import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Select,
  Space,
  message,
  Typography,
  Popconfirm,
  Alert,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { supabase } from "../../lib/supabase";
import { Agent } from "../../types/booking";
import {
  fetchAllAgentUsers,
  linkUserToAgent,
  unlinkAgentUser,
  AgentUserLink,
} from "../../services/agentUsersService";
import { getAllUsers } from "../../services/adminUsers";

const { Text } = Typography;

interface AuthUserOption {
  id: string;
  email: string;
}

export const AgentUsersManager: React.FC = () => {
  const [links, setLinks] = useState<AgentUserLink[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [users, setUsers] = useState<AuthUserOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState<string>();
  const [selectedAgentId, setSelectedAgentId] = useState<number>();
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const [{ data: linkData }, { data: agentData }] = await Promise.all([
      fetchAllAgentUsers(),
      supabase.from("booking_agent").select("*").order("name"),
    ]);
    setLinks(linkData);
    setAgents((agentData || []) as Agent[]);

    const { data: usersData, error: usersError } = await getAllUsers();
    if (!usersError && usersData?.users) {
      setUsers(
        usersData.users.map((u: { id: string; email: string }) => ({
          id: u.id,
          email: u.email,
        }))
      );
    } else if (usersError) {
      message.error("Failed to load users for dropdown");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const linkedUserIds = new Set(links.map((l) => l.user_id));
  const availableUsers = users.filter((u) => !linkedUserIds.has(u.id));

  const handleLink = async () => {
    if (!selectedUserId || !selectedAgentId) {
      message.warning("Select a user and an agent");
      return;
    }
    setSaving(true);
    const { error } = await linkUserToAgent(selectedUserId, selectedAgentId);
    if (error) {
      message.error("Failed to link user to agent");
    } else {
      message.success("Agent portal access granted");
      setSelectedUserId(undefined);
      setSelectedAgentId(undefined);
      load();
    }
    setSaving(false);
  };

  const handleUnlink = async (userId: string) => {
    const { error } = await unlinkAgentUser(userId);
    if (error) {
      message.error("Failed to remove agent access");
    } else {
      message.success("Agent access removed");
      load();
    }
  };

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Agent portal users sign in at /agent/login (hidden link). Each user must be linked to a booking_agent record."
      />

      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          placeholder="Select user"
          style={{ width: 320 }}
          showSearch
          optionFilterProp="label"
          value={selectedUserId}
          onChange={setSelectedUserId}
          loading={loading}
          options={availableUsers.map((u) => ({
            value: u.id,
            label: u.email,
          }))}
        />
        <Select
          placeholder="Select agent"
          style={{ width: 220 }}
          showSearch
          optionFilterProp="label"
          value={selectedAgentId}
          onChange={setSelectedAgentId}
          options={agents.map((a) => ({
            value: a.id,
            label: a.name,
          }))}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleLink}
          loading={saving}
        >
          Link user to agent
        </Button>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={links}
        pagination={false}
        columns={[
          {
            title: "User ID",
            dataIndex: "user_id",
            render: (id: string) => {
              const user = users.find((u) => u.id === id);
              return user ? (
                <>
                  {user.email}
                  <br />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {id}
                  </Text>
                </>
              ) : (
                id
              );
            },
          },
          {
            title: "Agent",
            key: "agent",
            render: (_, row) => {
              const agent = row.agent
                ? Array.isArray(row.agent)
                  ? row.agent[0]
                  : row.agent
                : agents.find((a) => a.id === row.agent_id);
              return agent?.name || row.agent_id;
            },
          },
          {
            title: "Linked",
            dataIndex: "created_at",
            render: (v: string) => new Date(v).toLocaleString(),
          },
          {
            title: "",
            key: "actions",
            render: (_, row) => (
              <Popconfirm
                title="Remove agent portal access?"
                onConfirm={() => handleUnlink(row.user_id)}
              >
                <Button danger size="small" icon={<DeleteOutlined />}>
                  Remove
                </Button>
              </Popconfirm>
            ),
          },
        ]}
      />
    </div>
  );
};
